/**
 * <g-task-item> - one task row of <g-task-list> (full mode).
 *
 * Laid out like a table row rather than a card: fixed-width leading and
 * trailing cells so that handle, checkbox, pills and due date all line up
 * vertically down the list, with the title taking the remaining space and
 * truncating instead of wrapping (a wrapped title would make one row taller
 * than its neighbours and break the table read).
 *
 * Cell order:
 *   [handle] [checkbox] [title ……… ⌄🌳2 +] [pill] [pill] … [due]
 *     1rem      auto      flex, min-w-0      8rem   8rem     7rem
 *
 * ONE CELL PER CONFIGURED PILL, so the header strip in g-task-list has a column
 * to sit above each. Widths come from ./columns.js so header and rows cannot
 * drift apart.
 *
 * A SUBTASK IS A TASK. There is no second, lesser kind of row: a task whose
 * parent is in the list is drawn by this same element, with the same checkbox,
 * pills, due date and tint, inside its parent's children container. The element
 * is therefore two parts:
 *
 *   <g-task-item>
 *     <div class="g-task-row">…</div>          ← render() owns this
 *     <div class="g-task-children">…</div>     ← g-task-list owns this
 *   </g-task-item>
 *
 * render() only ever replaces the ROW. The children container is created once
 * and left alone, because it holds live <g-task-item>s (and a Sortable bound to
 * it) that the list reconciles itself — rebuilding the element's innerHTML
 * would throw the whole subtree away on every refresh. Nesting the children
 * inside their parent is also what makes a dragged task carry its subtasks
 * with it.
 *
 * The children are indented by the container's left padding. The trailing
 * cells are fixed width at the row's right edge, which does not move, so pills
 * and due dates still line up with the header at every depth; only the title
 * cell gives up the width.
 *
 * Driven entirely by the `.task` view-model the parent sets. It observes NO
 * attributes and emits NO events — <g-task-list> owns the interaction
 * contract. (The parent sets a plain `task-id` attribute for event lookup.)
 *
 * @attr {string} task-id - set by the parent; used for event lookup only
 */
import { ROW_HOVER_CLASS } from '../util/status.js';
import { PILL_COL, DUE_COL } from './columns.js';

/** One indent step per level, as container padding. */
const CHILD_INDENT = 'pl-6';

export class GTaskItem extends HTMLElement {
  constructor() {
    super();
    this._task = null;
  }

  connectedCallback() {
    // Render on connect ONLY when this element owns a task.
    //
    // SortableJS builds its drag ghost with `cloneNode(true)` and appends it to
    // the document. A clone carries the markup but NOT the `.task` property —
    // properties do not clone — so a clone that rendered on connect would find
    // no task and erase itself, and the drag would carry an empty element.
    // On connect the markup already present IS the content; leave it alone.
    if (this._task) this.render();
  }

  get task() { return this._task; }
  set task(v) {
    this._task = v && typeof v === 'object' ? v : null;
    if (this.isConnected) this.render();
  }

  /**
   * The container this task's subtasks live in. Created on first use and kept
   * for the life of the element — see the class comment.
   */
  get childList() {
    let el = this._childEl;
    if (!el || el.parentNode !== this) {
      el = document.createElement('div');
      el.className = `g-task-children ${CHILD_INDENT} hidden`;
      el.setAttribute('data-g-task-children', '');
      this.appendChild(el);
      this._childEl = el;
    }
    return el;
  }

  /** Open or close the subtasks in place, without a re-render. */
  setExpanded(expanded) {
    const open = !!expanded;
    if (this._task) this._task.expanded = open;
    const hasKids = !!(this._task && this._task.childCount);
    this.childList.classList.toggle('hidden', !(open && hasKids));
    const btn = this._rowEl && this._rowEl.querySelector('[data-role="task-expand"]');
    if (btn) {
      btn.setAttribute('aria-expanded', String(open));
      btn.setAttribute('title', open ? 'Hide subtasks' : 'Show subtasks');
      const icon = btn.querySelector('[data-role="task-chevron"]');
      if (icon) icon.className = chevronClass(open);
    }
  }

  render() {
    const t = this._task;
    if (!t) { this.innerHTML = ''; this._rowEl = null; this._childEl = null; return; }

    // `select-none`: a text selection begun anywhere on the row swallows the
    // drag — the browser drags the selection instead of the task. Task text
    // stays copyable from the edit modal, where the title is an input.
    //
    // The separator is a TOP border. A subtask's first row sits directly under
    // its parent's row, inside the parent element, so a bottom border on the
    // element would draw under the whole subtree instead of under the row. Only
    // the first row of the whole list drops it, against the header.
    this.classList.add('block', 'select-none', 'border-t', 'border-base-300');
    this.classList.toggle('first:border-t-0', !t.depth);

    const wrap = document.createElement('div');
    wrap.innerHTML = rowHtml(t);
    const row = wrap.firstElementChild;
    if (this._rowEl && this._rowEl.parentNode === this) this._rowEl.replaceWith(row);
    else this.insertBefore(row, this.firstChild);
    this._rowEl = row;

    this.childList.classList.toggle('hidden', !(t.expanded && t.childCount));
  }
}

function rowHtml(t) {
  // Handle is hidden until the row is hovered (or something inside it is
  // focused from the KEYBOARD, so keyboard users can still reach it).
  // `:focus-visible`, not `focus-within`: a mouse click on the badge or the
  // add button leaves focus on it, which pinned both controls visible after
  // the pointer had left the row.
  const handle = `<span class="g-task-handle w-4 shrink-0 text-center cursor-grab text-base-content/30 hover:text-base-content/70
      opacity-0 group-hover:opacity-100 group-has-[:focus-visible]:opacity-100 transition-opacity"
      title="Drag to reorder"><i class="fa-solid fa-grip-vertical"></i></span>`;

  const title = escapeHtml(String(t.title ?? ''));

  // Expander and count in one control, next to the title it belongs to. The
  // count is how many tasks sit directly under this one; their own progress is
  // on their own rows, one level down.
  //
  // The -0.065em nudge levels the numeral with the icons: a digit's ink sits
  // 0.78px below its box centre at 12px (it reserves a descender it does not
  // use), while a Font Awesome glyph's ink is centred in its box. No
  // line-height fixes that; only moving the ink does.
  const expanded = !!t.expanded;
  const counter = t.childCount
    ? `<button type="button" data-role="task-expand" aria-expanded="${expanded}"
         title="${expanded ? 'Hide' : 'Show'} subtasks"
         class="badge badge-ghost badge-sm gap-1 tabular-nums whitespace-nowrap shrink-0 cursor-pointer hover:opacity-70 transition-opacity">
         <i data-role="task-chevron" class="${chevronClass(expanded)}"></i>
         <i class="fa-solid fa-list-tree text-[0.6875rem] leading-none text-primary"></i>
         <span class="leading-none" style="transform:translateY(-0.065em)">${t.childCount}</span></button>`
    : '';

  // "Add a subtask" asks the host to open its create form with this task as the
  // parent — a subtask is a task, so it is created where tasks are created.
  // Revealed on hover like the drag grip, and holding its space while
  // invisible so the title does not reflow when the pointer arrives.
  const addSub = t.canAddSubtask
    ? `<button type="button" data-role="subtask-add" title="Add subtask" aria-label="Add subtask"
         class="btn btn-primary btn-xs btn-square shrink-0
                opacity-0 group-hover:opacity-100 group-has-[:focus-visible]:opacity-100 transition-opacity">
         <i class="fa-solid fa-plus text-[0.625rem] leading-none"></i></button>`
    : '';

  const due = t.due
    ? `<span class="badge badge-sm ${dueClass(t.due.tone)} gap-1 whitespace-nowrap"><i class="fa-regular fa-calendar"></i>${escapeHtml(t.due.label)}</span>`
    : '';
  // The DUE CELL is a property of the list, not of this row: <g-task-list>
  // decides once and tells every row, so a task with no date still holds the
  // column open and the rows below it stay square with the heading.
  const showDue = t.showDue !== undefined ? !!t.showDue : !!t.due;

  // Long values ellipsis on a plain inline child: daisyUI's `.badge` is
  // inline-flex, and `text-overflow` does nothing on a flex container. `title`
  // carries the full value.
  const chipHtml = (p) => {
    const label = escapeHtml(String(p.label));
    return `<span class="badge badge-soft badge-sm shrink min-w-0 max-w-full overflow-hidden${p.color ? ` badge-${p.color}` : ''}" title="${label}"><span class="truncate">${label}</span></span>`;
  };
  // A group with no chips still renders its (empty) cell, or every row below
  // shifts left and the header starts labelling the wrong thing.
  const pills = (Array.isArray(t.pillGroups) ? t.pillGroups : []).map((g) =>
    `<span class="hidden sm:flex ${PILL_COL} shrink-0 min-w-0 gap-1 items-center overflow-hidden">${
      (g.chips || []).map(chipHtml).join('')
    }</span>`).join('');

  // Tint and hover both come from the stylesheet util/status.js injects. A
  // tinted row gets its hover from [data-g-status]:hover, so it must NOT also
  // carry the plain row-hover class or the two would fight.
  const statusAttr = t.statusAttr || '';
  const rowHover = statusAttr ? '' : ` ${ROW_HOVER_CLASS}`;

  return `
    <div class="g-task-row group flex items-center gap-2 px-2 py-1.5 min-w-0 cursor-pointer transition-colors${rowHover}"${statusAttr}>
      ${handle}
      <input type="checkbox" class="checkbox checkbox-sm shrink-0" data-role="task-toggle" aria-label="Done"${t.done ? ' checked' : ''}>
      <span class="flex-1 min-w-0 flex items-center gap-2">
        <span class="min-w-0 truncate text-sm ${t.done ? 'line-through opacity-60' : ''}" title="${title}">${title}</span>
        ${counter}
        ${addSub}
      </span>
      ${pills}
      ${showDue ? `<span class="${DUE_COL} shrink-0 text-right">${due}</span>` : ''}
    </div>`;
}

// The chevron's classes, in ONE place: render() writes it and setExpanded()
// rewrites it in place on every toggle.
function chevronClass(expanded) {
  return `fa-solid fa-chevron-${expanded ? 'down' : 'right'} text-[0.625rem] leading-none opacity-50`;
}

function dueClass(tone) {
  return tone === 'overdue' ? 'badge-error' : tone === 'soon' ? 'badge-warning' : 'badge-ghost';
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

if (!customElements.get('g-task-item')) {
  customElements.define('g-task-item', GTaskItem);
}
